import * as React from "react";
import { useRef, useEffect, useState } from "react";
import { useDispatch, useSelector, RootStateOrAny } from "react-redux";
import { useTranslation } from "react-i18next";
import {
    DetailsListLayoutMode,
    IColumn,
    Selection,
    SelectionMode,
} from "office-ui-fabric-react/lib/DetailsList";
import { MarqueeSelection } from "office-ui-fabric-react/lib/MarqueeSelection";
import { ShimmeredDetailsList, Stack } from "office-ui-fabric-react";
import { IRenderProps } from "../../orders/providers/Pagination.types";
import {
    setActiveStoreDoc,
    setStoreDocs,
    toggleAddStoreDocPanel,
} from "../../../config/redux/storeDocs/storeDocsActions";
import { sort, toggleFiltration } from "../../../config/redux/filters/filterActions";
import { storeDocsColumns } from "../columns/storeDocsColumns";
import Pagination from "../providers/Pagination";
import { footer, listContainerS, listS } from "../../clients/components/clientsList.styles";
import { IStoreDocsListProps } from "../models/StoreDocsListTypes";

const StoreDocsList: React.FC<IStoreDocsListProps> = ({ isPanelOpen }) => {
    const { t } = useTranslation("admin");
    const dispatch = useDispatch();
    const { selectedStoreDocs, reload } = useSelector((state: RootStateOrAny) => state.storeDocs);

    const [cols, setCols] = useState<IColumn[]>(() => storeDocsColumns(t));

    const footerRef = useRef<HTMLDivElement>(null);

    const selection = useRef(new Selection({
        onSelectionChanged: () => {
            const items = selection.current.getSelection();
            const ids = items.map((item: any) => ({
                id: item.id,
                documentStatus: item.documentStatus,
                fileExists: item.fileExists,
                blobFileName: item.blobFileName,
                documentStatusStr: item.documentStatusStr,
                documentDate: item.documentDate,
                description: item.description,
                documentTypeName: item.documentTypeName,
                documentType: item.documentType,
                companyName: item.companyName,
                receptionPlaceName: item.receptionPlaceName,
                internalStoreName: item.internalStoreName,
                containersNumber: item.containersNumber,
            }));
            dispatch(setStoreDocs({ selectedStoreDocs: [...ids] }));
        },
    }));

    useEffect(() => {
        setCols(storeDocsColumns(t));
    }, [t]);

    useEffect(() => {
        if (reload || !selectedStoreDocs[0]) {
            selection.current.selectToKey("", true);
        }
    }, [reload, selectedStoreDocs]);

    useEffect(() => {
        return () => {
            dispatch(setStoreDocs({ selectedStoreDocs: [] }));
        };
    }, []);

    const onItemInvoked = (item: any) => {
        dispatch(setActiveStoreDoc({ activeStoreDocs: item }));
        dispatch(toggleAddStoreDocPanel({ isOpen: true, add: false }));
    };

    const onColumnHeaderClick = (_: any, column: any) => {
        if (column.key === "receptionPlacesOrderTypes") return;

        const newColumns = cols.slice();
        const currColumn = newColumns.find(c => c.key === column.key)!;
        newColumns.forEach(col => {
            if (col === currColumn) {
                currColumn.isSortedDescending = !currColumn.isSortedDescending;
                currColumn.isSorted = true;
            } else {
                col.isSorted = false;
                col.isSortedDescending = true;
            }
        });
        dispatch(toggleFiltration({ filter: true }));
        dispatch(sort({ column: currColumn }));
        setCols(newColumns);
    };

    return (
        <>
            <Pagination
                footerRef={footerRef}
                render={({ data, loading }: IRenderProps) => (
                    <>
                        <Stack styles={listContainerS}>
                            <MarqueeSelection isEnabled={!isPanelOpen} selection={selection.current}>
                                <ShimmeredDetailsList
                                    items={data}
                                    columns={cols}
                                    setKey="items"
                                    layoutMode={DetailsListLayoutMode.justified}
                                    selection={selection.current}
                                    selectionMode={SelectionMode.single}
                                    selectionPreservedOnEmptyClick={true}
                                    onItemInvoked={onItemInvoked}
                                    usePageCache={true}
                                    styles={listS}
                                    onColumnHeaderClick={onColumnHeaderClick}
                                    enableShimmer={loading}
                                />
                            </MarqueeSelection>
                        </Stack>
                        <div className={footer} ref={footerRef} />
                    </>
                )}
            />
        </>
    );
};

export default StoreDocsList;